import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import LayoutBase from '../LayoutBase';
import { useSideMenuItems } from '../hooks';
import { SideMenuItem } from '../../molecules/SideMenu/types';
import { menuItems, sideMenuAdditionalItems } from './consts';
import { useIntl } from 'react-intl';
import ScPageHeader from '../../molecules/ScPageHeader';
import { useOnLogoutClick } from '../LayoutBase/hooks';
import routes from '../../../setup/consts/routes';
import {
  IconSettings,
  IconLayers,
  IconCash,
  IconSupport,
  IconMail,
} from '../../../assets/svg/icons';
import { mq } from '../../../theme/mediaQueries';

// ── styled ──────────────────────────────────────────────────────────────────

const StyledShell = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: var(--s-6);
  align-items: flex-start;

  ${mq.ltMd} {
    grid-template-columns: 1fr;
  }
`;

const StyledNav = styled.nav`
  position: sticky;
  top: calc(var(--topbar-h, 56px) + 24px);
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: var(--surface);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-1);
  padding: var(--s-3);

  ${mq.ltMd} {
    position: static;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 4px;
    border-radius: var(--r-md);
    padding: var(--s-3);
    margin-bottom: var(--s-4);
    box-shadow: none;
    border: 1px solid var(--line);
  }
`;

const StyledNavLink = styled(NavLink).attrs({ activeClassName: 'is-active' })`
  display: flex;
  align-items: center;
  gap: var(--s-2);
  padding: var(--s-2) var(--s-3);
  border-radius: var(--r-md);
  font-size: var(--t-md);
  font-weight: 500;
  color: var(--muted);
  text-decoration: none;
  transition: background var(--dur-fast) var(--ease-out),
    color var(--dur-fast) var(--ease-out),
    box-shadow var(--dur-fast) var(--ease-out);

  &:hover:not(.is-active) {
    background: var(--paper-2);
    color: var(--ink);
  }

  &.is-active {
    background: var(--surface);
    color: var(--ink);
    font-weight: 600;
    box-shadow: var(--sh-1);
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: var(--muted-2);
  }

  &.is-active svg {
    color: var(--brand-green);
  }
`;

const StyledSignOutButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--s-2);
  height: 40px;
  padding: 0 var(--s-3);
  border-radius: var(--r-sm);
  font: inherit;
  font-size: var(--t-sm);
  font-weight: var(--weight-medium);
  color: var(--danger);
  background: transparent;
  border: none;
  cursor: pointer;
  margin-top: var(--s-4);
  transition: background var(--dur-fast) var(--ease-out);

  &:hover {
    background: var(--danger-soft);
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const StyledNavDivider = styled.div`
  height: 1px;
  background: var(--line);
  margin: var(--s-1) 0;
`;

const StyledContent = styled.div`
  min-width: 0;
`;

// ── inline power icon ────────────────────────────────────────────────────────

const IconSignOut = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2} strokeLinecap='round' strokeLinejoin='round'>
    <path d='M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' />
    <polyline points='16 17 21 12 16 7' />
    <line x1='21' y1='12' x2='9' y2='12' />
  </svg>
);

// ── nav items ────────────────────────────────────────────────────────────────

interface NavItem {
  to: string;
  label: string;
  Icon: React.FC;
  isActive?: (match: unknown, location: { pathname: string }) => boolean;
}

// ── component ────────────────────────────────────────────────────────────────

const SettingsLayout: React.FC = ({ children }) => {
  const intl = useIntl();
  const onLogout = useOnLogoutClick();

  const NAV_ITEMS: NavItem[] = [
    { to: routes.ACCOUNT_DETAILS, label: intl.formatMessage({ id: 'sidebar.settings.accountDetails', defaultMessage: 'Account details' }), Icon: IconSettings },
    {
      to: routes.ENTITIES,
      label: intl.formatMessage({ id: 'sidebar.settings.entitiesAndUsers', defaultMessage: 'Entities & Users' }),
      Icon: IconLayers,
      isActive: (_match, { pathname }) =>
        [routes.ENTITIES, routes.USERS].includes(pathname),
    },
    { to: routes.BILLING_AND_PAYMENTS, label: intl.formatMessage({ id: 'sidebar.settings.billingAndPayments', defaultMessage: 'Billing & Payments' }), Icon: IconCash },
    { to: routes.SUPPORT_AND_FAQ, label: intl.formatMessage({ id: 'sidebar.settings.supportAndFaq', defaultMessage: 'Support & FAQ' }), Icon: IconSupport },
    { to: routes.CONTACT_US, label: intl.formatMessage({ id: 'sidebar.common.contactUs', defaultMessage: 'Contact Us' }), Icon: IconMail },
  ];

  const menuAdditionalItems = useMemo(
    () => sideMenuAdditionalItems(intl),
    [intl]
  );

  const items = useSideMenuItems(menuItems as unknown as SideMenuItem[]);

  return (
    <LayoutBase
      {...{ items }}
      settingsItems={items}
      additionalItems={menuAdditionalItems}
    >
      <ScPageHeader
        title={intl.formatMessage({ id: 'sidebar.settings.title', defaultMessage: 'Settings' })}
        subtitle={intl.formatMessage({ id: 'sidebar.settings.subtitle', defaultMessage: 'Account, entities, billing and support' })}
      />
      <StyledShell>
        <StyledNav>
          {NAV_ITEMS.map(({ to, label, Icon, isActive }) => (
            <StyledNavLink key={to} to={to} exact isActive={isActive}>
              <Icon />
              {label}
            </StyledNavLink>
          ))}
          <StyledNavDivider />
          <StyledSignOutButton type='button' onClick={onLogout}>
            <IconSignOut />
            {intl.formatMessage({ id: 'menu.logout', defaultMessage: 'Sign out' })}
          </StyledSignOutButton>
        </StyledNav>
        <StyledContent>{children}</StyledContent>
      </StyledShell>
    </LayoutBase>
  );
};

export default SettingsLayout;
